import React from 'react';
import { useMemo, useState } from 'react';
import { resolveSchema } from '../utils/openapi';

function SchemaNode({ label, schema, spec, required = false, depth = 0 }) {
  const resolved = resolveSchema(spec, schema) || schema;
  const hasChildren =
    !!resolved?.properties ||
    !!resolved?.items ||
    !!resolved?.oneOf ||
    !!resolved?.anyOf ||
    resolved?.type === 'object';

  const [expanded, setExpanded] = useState(depth < 1);

  const typeLabel = useMemo(() => {
    if (!resolved) {
      return 'unknown';
    }

    if (resolved.$ref) {
      return resolved.$ref.split('/').pop();
    }

    if (resolved.type === 'array') {
      const itemType = resolved.items?.type || (resolved.items?.$ref ? 'ref' : 'unknown');
      return `array<${itemType}>`;
    }

    return resolved.type || 'object';
  }, [resolved]);

  const requiredSet = new Set(resolved?.required || []);
  const objectEntries = Object.entries(resolved?.properties || {});

  return (
    <div className="font-mono text-xs text-slate-700 dark:text-slate-200">
      <div className="flex items-center gap-2 py-1">
        {hasChildren ? (
          <button
            type="button"
            className="h-5 w-5 rounded border border-slate-300 text-[10px] leading-none text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            onClick={() => setExpanded((previous) => !previous)}
            aria-label={expanded ? 'Collapse schema node' : 'Expand schema node'}
          >
            {expanded ? '-' : '+'}
          </button>
        ) : (
          <span className="inline-block h-5 w-5" aria-hidden="true" />
        )}

        <span className="font-semibold text-slate-900 dark:text-slate-50">{label}</span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {typeLabel}
        </span>
        {required ? <span className="text-[10px] font-semibold text-rose-600">required</span> : null}
      </div>

      {expanded && hasChildren ? (
        <div className="ml-6 border-l border-slate-200 pl-3 dark:border-slate-700">
          {objectEntries.map(([key, value]) => (
            <SchemaNode
              key={`${label}.${key}`}
              label={key}
              schema={value}
              spec={spec}
              required={requiredSet.has(key)}
              depth={depth + 1}
            />
          ))}

          {resolved?.items ? (
            <SchemaNode
              label="items"
              schema={resolved.items}
              spec={spec}
              required={false}
              depth={depth + 1}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SchemaTree({ title, schema, spec }) {
  const resolved = resolveSchema(spec, schema);

  if (!resolved) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
        No schema available.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-900/60">
      <h4 className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</h4>
      <SchemaNode label="root" schema={resolved} spec={spec} required={false} />
    </div>
  );
}

export default SchemaTree;
